import {Injectable} from '@angular/core';
import {NavigationExtras, Router} from '@angular/router';
import {RoutePathNames, RoutePaths} from '../../app.routes';
import {REDIRECT_URL} from 'patja-ui';

@Injectable({
  providedIn: 'root',
})
export class NavigationService {
  constructor(private router: Router) {}

  goToLogin(): void {
    this.router.navigateByUrl(`${RoutePaths.Login}`);
  }

  goToRegister(): void {
    this.router.navigateByUrl(RoutePaths.Register);
  }

  goToResetPassword(email?: string): void {
    const navigationExtras: NavigationExtras = {
      state: {
        email: email,
      },
    };
    this.router.navigateByUrl(RoutePaths.ResetPassword, navigationExtras);
  }

  goToVerify(email: string, parent: string): void {
    this.router.navigateByUrl(`${parent}/${RoutePaths.Verify}?email=${encodeURIComponent(email)}`);
  }

  goToHome(shouldReload?: boolean): void {
    const reportId: string | null = sessionStorage.getItem('redirectToReport');
    const redirectUrl: string | null = sessionStorage.getItem(REDIRECT_URL);

    if (redirectUrl && !reportId) {
      this.router.navigateByUrl(redirectUrl);
      sessionStorage.removeItem(REDIRECT_URL);
    } else if (reportId) {
      // We came from email link and need to navigate to the report preview after login (2FA) flow
      this.goToViewReport(reportId);
      sessionStorage.removeItem('redirectToReport');
    } else {
      this.router.navigateByUrl(`${RoutePaths.Home}`).then(() => {
        if (shouldReload) {
          location.reload();
        }
      });
    }
  }

  goToSettings(): void {
    this.router.navigateByUrl(`/${RoutePathNames.Settings}`);
  }

  goToSetNewPassword(email: string, key: string): void {
    this.router.navigateByUrl(
      `${RoutePaths.ResetPassword}/${RoutePathNames.SetNewPassword}?key=${key}&email=${encodeURIComponent(email)}`,
    );
  }

  goToLinkExpired(email: string): void {
    this.router.navigateByUrl(
      `${RoutePaths.ResetPassword}/${RoutePathNames.ResetPasswordLinkExpired}?email=${encodeURIComponent(email)}`,
    );
  }

  goToSessionExpired(): void {
    this.router.navigateByUrl(RoutePaths.SessionExpired);
  }

  goToChooseAuthMethod(): void {
    this.router.navigateByUrl(RoutePaths.ChooseMethod);
  }

  goToEnableTwoFactorAuth(): void {
    this.router.navigateByUrl(RoutePaths.EnableTwoFactor);
  }

  goToGoogleAuthSetup(): void {
    this.router.navigateByUrl(RoutePaths.GoogleAuthenticator);
  }

  goToVerifyGoogleAuth(): void {
    this.router.navigateByUrl(RoutePaths.VerifyGoogleAuthenticator);
  }

  goToBackupCodes(): void {
    this.router.navigateByUrl(RoutePaths.TwoFactorBackupCodes);
  }

  goToTwoFactorOptions(): void {
    this.router.navigateByUrl(RoutePaths.TwoFactorOptions);
  }

  goToVerifyBackupCode(): void {
    this.router.navigateByUrl(RoutePaths.VerifyBackupCode);
  }

  goToVerified(isAlreadyVerified?: boolean): void {
    this.router.navigateByUrl(`${RoutePaths.Verified}?isAlreadyVerified=${isAlreadyVerified}`);
  }

  goToPrivacyPolicy(): void {
    this.router.navigateByUrl(RoutePathNames.PrivacyPolicy);
  }

  goToTermsOfUse(): void {
    this.router.navigateByUrl(RoutePathNames.TermsOfUse);
  }

  goToReports(): void {
    this.router.navigateByUrl(`${RoutePathNames.Reports}`);
  }

  goToViewReport(reportId: string): void {
    this.router.navigateByUrl(`${RoutePathNames.Reports}/${reportId}`);
  }

  goToNewReport(): void {
    this.router.navigateByUrl(`${RoutePaths.Reports}/new`);
  }

  goToSalesInvoices(): void {
    this.router.navigateByUrl(RoutePaths.SalesInvoices);
  }

  goToSalesInvoiceById(invoiceId: string): void {
    this.router.navigateByUrl(`${RoutePaths.SalesInvoices}/${invoiceId}`);
  }

  goToPurchaseInvoiceById(
    invoiceId: string,
    companyId?: string,
    provider?: 'netvisor' | 'kitsas' | 'odoo',
  ): void {
    const queryParams: Record<string, string> = {};
    if (companyId) {
      queryParams['companyId'] = companyId;
    }
    if (provider) {
      queryParams['provider'] = provider;
    }
    const navigationExtras: NavigationExtras = Object.keys(queryParams).length ? {queryParams} : {};
    this.router.navigate([RoutePaths.PurchaseInvoices, invoiceId], navigationExtras);
  }

  goToPlattaPurchaseInvoiceById(invoiceId: string, companyId: string): void {
    this.router.navigate([RoutePaths.PurchaseInvoices, invoiceId], {
      queryParams: {companyId, source: 'platta'},
    });
  }

  goToCreateSalesInvoice(): void {
    this.router.navigateByUrl(`${RoutePathNames.SalesInvoices}/new`);
  }

  goToCreatePurchaseInvoice(): void {
    this.router.navigateByUrl(`${RoutePathNames.PurchaseInvoices}/new`);
  }

  goToPurchaseInvoices(): void {
    this.router.navigateByUrl(RoutePaths.PurchaseInvoices);
  }

  goToReceipts(): void {
    this.router.navigateByUrl(this.getReceiptListPath());
  }

  goToReceiptById(receiptId: string): void {
    this.router.navigateByUrl(`${this.getReceiptListPath()}/${receiptId}`);
  }

  getReceiptListPath(): RoutePaths.Receipts | RoutePaths.StatementAndMaterial {
    return this.router.url.split(/[/?#]/)[1] === RoutePathNames.StatementAndMaterial
      ? RoutePaths.StatementAndMaterial
      : RoutePaths.Receipts;
  }

  goToCreateReceipt(): void {
    this.router.navigateByUrl(`${RoutePathNames.Receipts}/new`);
  }

  goToEditReceipt(receiptId: string): void {
    this.router.navigateByUrl(`${this.getReceiptListPath()}/${receiptId}/edit`);
  }

  goToEditInvoice(invoiceId: string): void {
    this.router.navigateByUrl(`${RoutePathNames.SalesInvoices}/${invoiceId}/edit`);
  }

  goToAccountingMaterials(): void {
    this.router.navigateByUrl(RoutePaths.AccountingMaterial);
  }

  goToAccountingMaterialById(id: string): void {
    this.router.navigateByUrl(`${RoutePaths.AccountingMaterial}/${id}`);
  }
}
